/* STATE */
import { call, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { AppAction, createActionType, createApiActionCreators, RequestActionTypes } from 'helpers/redux/reduxHelpers';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';
import { setInvoiceNumberAction } from 'saga/billingManagement/claims/ducks';

export interface ClaimStep1Payload {
  invoiceNumber?: string;
  invoiceNumberNew?: string;
  claimId?: number;
  serviceId?: number;
  productNo?: string;
  createDate?: string;
  posNext?: number;
}

/* ACTION TYPES */
export enum ClaimStep1StateTypes {
  GetData = '@@ClaimStep1/GET_DATA',
  GetLazyLoad = '@@ClaimStep1/GET_LAZY_LOAD',
  SaveClaimStep1 = '@@ClaimStep1/SAVE_CLAIM_STEP_1',
  Step1changeInvoice = '@@ClaimStep1/STEP_1_CHANGE_INVOICE',
}

/* ACTIONS */
export const getClaimStep1DataActions = createApiActionCreators(ClaimStep1StateTypes.GetData);
export const getClaimStep1LazyLoadActions = createApiActionCreators(ClaimStep1StateTypes.GetLazyLoad);
export const saveClaimStep1Actions = createApiActionCreators(ClaimStep1StateTypes.SaveClaimStep1);
export const step1changeInvoiceActions = createApiActionCreators(ClaimStep1StateTypes.Step1changeInvoice);

/* SAGAS */

function* getData({ payload }: AppAction<ClaimStep1Payload>) {
  if (!payload.invoiceNumber) return;

  const pageSize = Number(selectProperty('gui.billing.MonthStatementClaim.step1.pageSize'));
  const request: ApiClientRequest = {
    apiCall: call(
      api.getData,
      yield getApiUrl(environment.urlConfig.billingManagement),
      Number(environment.properties['api.request.timeoutInSec.ebill.step1']) * 1000 || 30000,
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.productNo,
      null,
      pageSize
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getClaimStep1DataActions.success(resp.data));
  } else {
    yield put(getClaimStep1DataActions.failure());
  }
}

function* lazyLoad({ payload }: AppAction<ClaimStep1Payload>) {
  if (!payload.invoiceNumber) return;

  const pageSize = Number(selectProperty('gui.billing.MonthStatementClaim.step1.pageSize'));
  const request: ApiClientRequest = {
    apiCall: call(
      api.lazyload,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.productNo,
      payload.posNext,
      pageSize
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getClaimStep1LazyLoadActions.success(resp.data));
  } else {
    yield put(getClaimStep1LazyLoadActions.failure());
  }
}

function* saveClaimStep1({ payload }: AppAction<ClaimStep1Payload>) {
  if (payload.serviceId === null || payload.serviceId === undefined) {
    yield put(getKahlActions.request({ infoMessageIds: 'ID1376' }));

    return;
  }

  if (!payload.invoiceNumber || !payload.claimId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.saveClaimStep1,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.createDate
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(saveClaimStep1Actions.success(resp.data));
  } else {
    yield put(saveClaimStep1Actions.failure());
  }
}

function* step1changeInvoice({ payload }: AppAction<ClaimStep1Payload>) {
  if (!payload.invoiceNumber || !payload.invoiceNumberNew || !payload.claimId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.step1changeInvoice,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.invoiceNumberNew,
      payload.claimId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(step1changeInvoiceActions.success());
    yield put(
      getClaimStep1DataActions.request({
        invoiceNumber: payload.invoiceNumberNew,
        claimId: resp.data?.id,
      })
    );
    yield put(setInvoiceNumberAction(payload.invoiceNumberNew));
  } else {
    yield put(step1changeInvoiceActions.failure());
  }
}

/* EXPORT */
export function* claimStep1Saga() {
  yield takeLatest(createActionType(ClaimStep1StateTypes.GetData, RequestActionTypes.REQUEST), getData);
  yield takeLatest(createActionType(ClaimStep1StateTypes.GetLazyLoad, RequestActionTypes.REQUEST), lazyLoad);
  yield takeLatest(createActionType(ClaimStep1StateTypes.SaveClaimStep1, RequestActionTypes.REQUEST), saveClaimStep1);
  yield takeLatest(
    createActionType(ClaimStep1StateTypes.Step1changeInvoice, RequestActionTypes.REQUEST),
    step1changeInvoice
  );
}
